import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, PawPrint, FolderOpen, Plus, ArrowLeft, Dog, Cat, Bird } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIES_ICON = {
  Dog: Dog, dog: Dog,
  Cat: Cat, cat: Cat,
  Bird: Bird, bird: Bird,
};

const SpeciesIcon = ({ species }) => {
  const Icon = SPECIES_ICON[species] || PawPrint;
  return <Icon className="w-6 h-6" />;
};


/** Pet photo thumbnail — fetches from the authenticated /pets/{id}/photo
 *  endpoint and shows a blob URL, falling back to the species icon. */
const PetPhoto = ({ pet, password }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!pet?.photo_path) return;
    let objUrl = null;
    fetch(`${API_URL}/api/admin/pets/${pet.id}/photo`, {
      headers: { Authorization: `Basic ${btoa(':' + password)}` },
    })
      .then((r) => r.ok ? r.blob() : Promise.reject())
      .then((b) => { objUrl = URL.createObjectURL(b); setUrl(objUrl); })
      .catch(() => setFailed(true));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [pet?.id, pet?.photo_path, password]);

  if (!pet?.photo_path || failed || !url) {
    return (
      <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
        <SpeciesIcon species={pet?.species} />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={pet.name}
      className="w-12 h-12 rounded-lg object-cover group-hover:scale-110 transition-transform"
    />
  );
};

export const PetsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientFilter = searchParams.get('client_id');
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [migrating, setMigrating] = useState(false);
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    fetchPets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter]);

  useEffect(() => {
    const t = setTimeout(fetchPets, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchPets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (clientFilter) params.append('client_id', clientFilter);
      const url = `${API_URL}/api/admin/pets${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) setPets(await res.json());
      else if (res.status === 401) navigate('/admin/login');
    } catch (err) {
      logger.error('pets fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async () => {
    if (!window.confirm('Scan existing relocations and auto-create pet folders from them? Safe to run multiple times (idempotent).')) return;
    setMigrating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrations/pets/from-relocations`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) {
        const data = await res.json();
        alert(
          `Migration complete:\n` +
          `• ${data.pets_created} new pet${data.pets_created === 1 ? '' : 's'} created\n` +
          `• ${data.pets_already_linked} already in system\n` +
          `• ${data.documents_migrated} document${data.documents_migrated === 1 ? '' : 's'} linked`
        );
        fetchPets();
      } else {
        alert('Migration failed');
      }
    } finally {
      setMigrating(false);
    }
  };

  // Group pets by client
  const petsByClient = pets.reduce((acc, pet) => {
    const key = pet.client_id;
    if (!acc[key]) acc[key] = { client_name: pet.client_name, client_email: pet.client_email, pets: [] };
    acc[key].pets.push(pet);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
              <PawPrint className="w-6 h-6" />
              Pet Filing System
            </h1>
            <p className="text-sm text-foreground/60">
              Permanent pet folders with documents, vaccination records, and travel history.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runMigration}
              disabled={migrating}
              data-testid="migrate-from-relocations"
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              title="Scan existing relocations and auto-create pet folders"
            >
              {migrating ? 'Migrating…' : 'Import from Relocations'}
            </button>
            <button
              onClick={() => navigate('/admin/pets/new')}
              data-testid="new-pet-btn"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Pet
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" /> Back
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6">
        {/* Search */}
        <div className="relative mb-6 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by pet name, breed, microchip, or client…"
            data-testid="pets-search"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-foreground/60">Loading pets…</p>
          </div>
        ) : pets.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-12 text-center">
            <PawPrint className="w-16 h-16 text-foreground/30 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-1">No pets yet</h2>
            <p className="text-sm text-foreground/60">
              Create a pet or run <em>Import from Relocations</em> to backfill from past trips.
            </p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="pets-by-client">
            {Object.entries(petsByClient).map(([clientId, group]) => (
              <section key={clientId}>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="font-semibold text-foreground">{group.client_name}</h2>
                  <span className="text-xs text-foreground/60 font-mono">{group.client_email}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {group.pets.map((pet) => (
                    <button
                      key={pet.id}
                      onClick={() => navigate(`/admin/pets/${pet.id}`)}
                      data-testid={`pet-card-${pet.id}`}
                      className="group text-left bg-white border border-border rounded-xl p-5 hover:border-primary hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <PetPhoto pet={pet} password={password} />
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-foreground truncate">{pet.name}</h3>
                          <p className="text-xs text-foreground/60">
                            {pet.species}{pet.breed ? ` · ${pet.breed}` : ''}
                          </p>
                        </div>
                        <FolderOpen className="w-5 h-5 text-foreground/30 group-hover:text-primary flex-shrink-0" />
                      </div>
                      <div className="text-xs text-foreground/60 space-y-1">
                        {pet.microchip_number && (
                          <p className="font-mono truncate">🔗 {pet.microchip_number}</p>
                        )}
                        <p>Added {formatDate(pet.created_at)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
